import { useEffect, useMemo, useRef, useState } from "react";

import { useOnClickOutside } from "src/hooks/usehooks-ts";

/** Shared state and behaviour for popover list editors (Enum, Tags). */
export const useOptionListEditor = (args: {
  options: string[];
  onBlur: () => void;
  initialIndex?: number;
}) => {
  const [isAddingValue, setIsAddingValue] = useState(false);
  const [search, setSearch] = useState("");
  const [rawFocusedIndex, setRawFocusedIndex] = useState(
    args.initialIndex ?? 0,
  );
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    if (!isAddingValue || !search) return args.options;
    const lower = search.toLowerCase();
    return args.options.filter((o) => o.toLowerCase().includes(lower));
  }, [args.options, search, isAddingValue]);

  // Clamp so a stale index is never used by handlers or rendered
  const focusedIndex = Math.min(rawFocusedIndex, filteredOptions.length);
  const totalItems = filteredOptions.length + 1; // +1 for "Add value"

  // Auto-focus the list when not in add-value mode
  useEffect(() => {
    if (!isAddingValue) {
      requestAnimationFrame(() => listRef.current?.focus());
    }
  }, [isAddingValue]);

  useOnClickOutside([triggerRef, listRef], args.onBlur);

  const scrollFocusedIntoView = (index: number) => {
    const items = listRef.current?.querySelectorAll("[role='option']");
    items?.[index]?.scrollIntoView({ block: "nearest" });
  };

  const moveFocus = (direction: "up" | "down") => {
    const next =
      direction === "down"
        ? Math.min(focusedIndex + 1, totalItems - 1)
        : Math.max(focusedIndex - 1, 0);
    setRawFocusedIndex(next);
    scrollFocusedIntoView(next);
  };

  const exitAddMode = () => {
    setIsAddingValue(false);
    setSearch("");
  };

  return {
    isAddingValue,
    setIsAddingValue,
    search,
    setSearch,
    filteredOptions,
    focusedIndex,
    setFocusedIndex: setRawFocusedIndex,
    totalItems,
    listRef,
    triggerRef,
    moveFocus,
    exitAddMode,
  };
};
